import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  Instagram,
  Youtube,
  Linkedin,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useLanguage } from '../contexts/LanguageContext';
import WhatsAppIcon from '../components/ui/WhatsAppIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contato = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        toast({
          title: t('contact.successTitle'),
          description: t('contact.successDesc'),
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Error sending');
      }
    } catch (error) {
      toast({
        title: t('contact.errorTitle'),
        description: t('contact.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const offices = [
    {
      city: 'Sao Paulo',
      cityDisplay: 'São Paulo',
      isMatriz: true,
      address: 'Avenida Moema, 87 - Bl A, Sala 152 - Moema',
      cep: '04077-020'
    },
    {
      city: 'Porto Alegre',
      cityDisplay: 'Porto Alegre',
      isMatriz: false,
      address: 'R. Dom Pedro II, 1240 - São João sala 406',
      cep: '90550-141'
    },
    {
      city: 'Curitiba',
      cityDisplay: 'Curitiba',
      isMatriz: false,
      address: 'Av. Iguacu, 2820 - Batel',
      cep: '80240-030'
    }
  ];

  const subjects = [
    { key: 'contact.subjects.ma', value: 'M&A - Mergers & Acquisitions' },
    { key: 'contact.subjects.fiscal', value: 'Digital Tax Management' },
    { key: 'contact.subjects.positive', value: 'Positive Tax Management' },
    { key: 'contact.subjects.liabilities', value: 'Liability Management' },
    { key: 'contact.subjects.credit', value: 'Credit & Operations' },
    { key: 'contact.subjects.partners', value: 'Partner Program' },
    { key: 'contact.subjects.legal', value: 'Legal Advisory' },
    { key: 'contact.subjects.other', value: 'Other' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contato-page">
      <Toaster />
      
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t('contact.tag')}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6"
            >
              {t('contact.title')} <span className="gold-sheen">{t('contact.titleHighlight')}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-stone-600"
            >
              {t('contact.desc')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-serif font-bold text-stone-900 mb-8">
                {t('contact.infoTitle')}
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-sm">
                  <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald flex-shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">{t('contact.phone')}</p>
                    <div className="flex items-center gap-2">
                      <a
                        href="tel:+5511921313708"
                        className="text-stone-600 hover:text-bronze transition-colors"
                        data-testid="contact-phone"
                      >
                        +55 (11) 921 313 708
                      </a>
                      <a
                        href="https://wa.me/5511921313708"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#25D366] hover:opacity-80 transition-opacity"
                        data-testid="contact-whatsapp"
                        aria-label="WhatsApp"
                      >
                        <WhatsAppIcon className="h-5 w-5" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-sm">
                  <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald flex-shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">{t('contact.email')}</p>
                    <a 
                      href="mailto:contato@holdcorp.com.br" 
                      className="text-stone-600 hover:text-bronze transition-colors"
                      data-testid="contact-email"
                    >
                      contato@holdcorp.com.br
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-sm">
                  <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald flex-shrink-0">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">{t('contact.hours')}</p>
                    <p className="text-stone-600">{t('contact.hoursValue')}</p>
                  </div>
                </div>
              </div>

              {/* Offices */}
              <h3 className="text-xl font-serif font-semibold text-stone-900 mb-6">
                {t('contact.offices')}
              </h3>
              <div className="space-y-4">
                {offices.map((office) => (
                  <div 
                    key={office.city}
                    className="flex items-start gap-3 p-4 border border-stone-200 rounded-sm"
                    data-testid={`office-${office.city.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <MapPin className="h-5 w-5 text-bronze mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-stone-900">
                        {office.cityDisplay} {office.isMatriz ? `(${t('about.offices.matriz')})` : ''}
                      </p>
                      <p className="text-stone-600 text-sm">{office.address}</p>
                      <p className="text-stone-500 text-sm">{office.cep}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social */}
              <div className="mt-8">
                <p className="font-semibold text-stone-900 mb-4">{t('contact.social')}</p>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com/holder.corp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald hover:bg-emerald hover:text-white transition-colors"
                    data-testid="social-instagram"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a 
                    href="https://youtube.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald hover:bg-emerald hover:text-white transition-colors"
                    data-testid="social-youtube"
                    aria-label="YouTube"
                  >
                    <Youtube className="h-5 w-5" />
                  </a>
                  <a 
                    href="https://linkedin.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald hover:bg-emerald hover:text-white transition-colors"
                    data-testid="social-linkedin"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-stone-50 p-8 md:p-10 rounded-sm">
                <h2 className="text-2xl font-serif font-bold text-stone-900 mb-2">
                  {t('contact.formTitle')}
                </h2>
                <p className="text-stone-600 mb-8">
                  {t('contact.formDesc')}
                </p>

                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-emerald mx-auto mb-4" />
                    <h3 className="text-xl font-serif font-bold text-stone-900 mb-2">
                      {t('contact.successTitle')}
                    </h3>
                    <p className="text-stone-600 mb-6">
                      {t('contact.successDesc')}
                    </p>
                    <Button 
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-emerald text-emerald hover:bg-emerald hover:text-white"
                      data-testid="new-message-btn"
                    >
                      {t('contact.newMessage')}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">{t('contact.name')} *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">{t('contact.emailField')} *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">{t('contact.phoneField')}</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">{t('contact.company')}</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="mt-2"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject">{t('contact.subject')} *</Label>
                      <Select onValueChange={handleSelectChange} value={formData.subject}>
                        <SelectTrigger className="mt-2" data-testid="select-subject">
                          <SelectValue placeholder={t('contact.subjectPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map((subject) => (
                            <SelectItem key={subject.key} value={subject.value}>
                              {t(subject.key)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">{t('contact.message')} *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="mt-2"
                        data-testid="input-message"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-emerald hover:bg-emerald-light text-white rounded-sm py-6 text-lg font-serif btn-shine"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        t('contact.sending')
                      ) : (
                        <>
                          {t('contact.submit')}
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>

                    <p className="text-stone-500 text-xs text-center">
                      {t('contact.privacy')}
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;
